import {ReactNode, useRef} from 'react';
import Anchor from './Anchor';
import AnchorCardBg from './AnchorCardBg';
import AspectRatioBox from './AspectRatioBox';
import BadgeList from './BadgeList';
import PageImage from './PageImage';
import SingleText from './SingleText';
import Text from './Text';
import useContentVideo from '../../commonSub/hook/useContentVideo';
import useIsLite from '../../commonSub/hook/useIsLite';
import useScreenType from '../hook/useScreenType';
import useSplitAdultBadgeList from '../hook/useSplitAdultBadgeList';
import {IAnchorCard} from '../type';
import {PageInfo} from '../util/PageRouter';

interface Props {
  blackMaskOpacity?: string;
  content: IAnchorCard;
  pageInfo: PageInfo;
  desc?: string;
  titleText?: string;
  bottomElement?: ReactNode;
  labelBadgeElement?: ReactNode;
  isOnlyRoundedCard?: boolean;
  className?: string;
  mainImgClassName?: string;
  bgClassName?: string;
  customAspectRatio?: number;
  onClick?: () => void;
}

export default function AnchorCard({
  blackMaskOpacity,
  content,
  pageInfo,
  desc,
  titleText,
  bottomElement,
  labelBadgeElement,
  className,
  mainImgClassName,
  bgClassName,
  isOnlyRoundedCard = true,
  customAspectRatio,
  onClick,
}: Props) {
  const {
    badges,
    bgImg,
    mainImg,
    title,
    mainTitleImg,
    motionVideo,
    motionStartImg,
  } = content;
  const {isLarge} = useScreenType();
  const aspectRatio = customAspectRatio || (isLarge ? 298 / 608 : 230 / 353);

  const isLite = useIsLite();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const motionImgRef = useRef<HTMLPictureElement>(null);

  const {MotionVideoComponent} = useContentVideo({
    wrapperRef,
    motionImgRef,
    motionVideo,
    motionVideoPoster: motionStartImg,
    motionVideoAspectRatio: aspectRatio,
  });

  const {adultBadges, otherBadges} = useSplitAdultBadgeList(badges);

  return (
    <div
      ref={wrapperRef}
      className={cn('relative w-full h-full overflow-hidden', className)}
    >
      <Anchor pageInfo={pageInfo} className="cursor-pointer" onClick={onClick}>
        <AspectRatioBox
          wrapperClassName={cn({
            'rounded-12': isOnlyRoundedCard,
          })}
          aspectRatio={aspectRatio}
        >
          <AnchorCardBg
            src={bgImg}
            blackMaskOpacity={blackMaskOpacity}
            bgClassName={bgClassName}
          />
          {(isLite || !motionVideo) && (
            <PageImage
              ref={motionImgRef}
              className={cn(mainImgClassName, MOTION_IMAGE_CN)}
              alt={title}
              src={mainImg}
            />
          )}
          <div className="absolute left-18 top-1/2 -translate-y-1/2">
            <div className="flex items-center gap-2">
              <BadgeList
                className="flex gap-x-2 !w-auto"
                badges={otherBadges}
              />
              {labelBadgeElement}
              <BadgeList className="flex !w-auto" badges={adultBadges} />
            </div>
            {mainTitleImg && (
              <PageImage
                className="mt-8 max-w-[130px] lg:max-w-[170px]"
                alt={title}
                src={mainTitleImg}
              />
            )}
            {titleText && (
              <Text className="s19-black-white leading-23 -tracking-[0.4px] mt-8">
                {titleText}
              </Text>
            )}
          </div>
          {desc && (
            <SingleText
              className={cn(
                's12-regular-white opacity-80',
                'absolute left-0 bottom-16 px-18 z-3'
              )}
            >
              {desc}
            </SingleText>
          )}
          <MotionVideoComponent
            className={MOTION_IMAGE_CN}
            posterClassName={MOTION_IMAGE_CN}
          />
        </AspectRatioBox>
      </Anchor>
      {bottomElement}
    </div>
  );
}

const MOTION_IMAGE_CN =
  'absolute bottom-0 max-h-[calc(100%-20px)] -right-16 lg:right-18';
